import React from "react";
import { createPortal } from "react-dom";
import { MdClose } from "react-icons/md";
import { UI_DIVIDER } from "lib/uiClasses";

export type SlideOverProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Side the drawer slides from. Auto picks end/start for RTL when 'auto'. */
  side?: "start" | "end" | "auto";
  width?: "sm" | "md" | "lg";
};

const WIDTH_CLASS: Record<NonNullable<SlideOverProps["width"]>, string> = {
  sm: "w-full max-w-sm",
  md: "w-full max-w-md",
  lg: "w-full max-w-xl",
};

function isRtl() {
  return document.documentElement.getAttribute("dir") === "rtl";
}

export default function SlideOver({
  open,
  onClose,
  title,
  children,
  footer,
  side = "auto",
  width = "md",
}: SlideOverProps) {
  const [mounted, setMounted] = React.useState(false);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    // Double rAF for the enter transition after mount.
    setMounted(false);
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setMounted(true)));
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      setMounted(false);
    };
  }, [open]);

  if (!open) return null;

  const resolvedSide = side === "auto" ? (isRtl() ? "start" : "end") : side;
  const positional = resolvedSide === "end" ? "right-0" : "left-0";
  const enter = mounted ? "translate-x-0" : resolvedSide === "end" ? "translate-x-full" : "-translate-x-full";

  const content = (
    <div className="fixed inset-0" style={{ zIndex: 1300 }}>
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-200 dark:bg-black/65 ${
          mounted ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`absolute ${positional} top-0 flex h-full ${WIDTH_CLASS[width]} flex-col border-gray-200 bg-white shadow-2xl transition-transform duration-200 dark:border-white/[0.08] dark:bg-surface-elevated dark:text-slate-50 dark:shadow-modal-dark ${enter}`}
      >
        <div className={`flex items-center justify-between gap-4 border-b px-5 py-4 ${UI_DIVIDER}`}>
          <h3 className="text-base font-bold text-gray-800 dark:text-slate-50">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-slate-100"
            aria-label="Close"
          >
            <MdClose className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className={`flex justify-end gap-2 border-t px-5 py-3 ${UI_DIVIDER}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
